import { useCallback, useEffect, useMemo, useState } from 'react';
import { userSelector } from 'slices/auth.slice';
import { globalOrderSelector } from 'slices/field.slice';
import {
  BaseColumnConfig,
  createColumnsSelector,
  updateColumns,
} from 'slices/persistedColumns.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import {
  type Column,
  type ColumnConfig,
  SortColumnsModal,
} from '@timeedit/ui-components';
import { ColumnTitle } from '@timeedit/ui-components/components/EditableTable/ColumnTitle';

export type UnknownObject = Record<string, unknown>;
export type ColumnKey = keyof any;

export type TColumn<T extends object> = ColumnConfig &
  Pick<
    Column<T>,
    'resizable' | 'sortDirections' | 'sorter' | 'input' | 'render' | 'ellipsis'
  >;

export type TColumns<
  K extends ColumnKey = string,
  T extends object = UnknownObject,
> = Record<K, TColumn<T>>;

/** These two columns should appear first in object table */
const NON_FIELD_DEFAULT_COLUMNS = {
  extId: -2,
  types: -1,
} as const;

export const useSortColumnsModal = <
  K extends ColumnKey = string,
  T extends object = {},
>(
  settings: TColumns<K, T>
) => {
  const sortColumnsInFieldOrder = useFieldSort();

  const [isSortColumnsModalOpen, setIsSortColumnsModalOpen] = useState(false);
  const [sortColumnsModalSettings, setSortColumnsModalSettings] = useState<
    ColumnConfig[]
  >(() =>
    Object.entries<TColumn<T>>(settings)
      .map(([key, { label }]) => ({
        key,
        label,
      }))
      .sort(sortColumnsInFieldOrder)
  );

  useEffect(() => {
    setSortColumnsModalSettings(
      Object.entries<TColumn<T>>(settings)
        .map(([key, { label }]) => ({
          key,
          label,
        }))
        .sort(sortColumnsInFieldOrder)
    );
  }, [settings, sortColumnsInFieldOrder]);

  const openSortColumnsModal = () => setIsSortColumnsModalOpen(true);
  const closeSortColumnsModal = () => setIsSortColumnsModalOpen(false);

  const sortColumnsModal = useMemo(
    () => (
      <SortColumnsModal
        columnConfigs={sortColumnsModalSettings}
        open={isSortColumnsModalOpen}
        onConfirm={(newColumnConfig) => {
          setSortColumnsModalSettings(newColumnConfig);
          setIsSortColumnsModalOpen(false);
        }}
        onCancel={() => setIsSortColumnsModalOpen(false)}
      />
    ),
    [isSortColumnsModalOpen, sortColumnsModalSettings]
  );

  const sortedColumns: Column<T>[] = useMemo(
    () =>
      sortColumnsModalSettings
        .filter(({ hidden }) => !hidden)
        .map(({ key, label }) => ({
          title: <ColumnTitle title={label} />,
          dataIndex: key,
          ...settings[key],
        })),
    [settings, sortColumnsModalSettings]
  );

  return {
    sortColumnsModal,
    openSortColumnsModal,
    closeSortColumnsModal,
    sortedColumns,
  };
};

export const usePersistedColumnsModal = <
  K extends ColumnKey = string,
  T extends object = {},
>(
  settings: TColumns<K, T>,
  tableId: string,
  typeId?: number
) => {
  const columnsSelector = useMemo(createColumnsSelector, []);
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const organizationId = user?.organizationId;
  const userId = user?.id;

  const columnsConfig = useAppSelector((state) =>
    columnsSelector(state, tableId, typeId)
  );

  const [isSortColumnsModalOpen, setIsSortColumnsModalOpen] = useState(false);
  const openSortColumnsModal = () => setIsSortColumnsModalOpen(true);
  const closeSortColumnsModal = () => setIsSortColumnsModalOpen(false);

  const combinedColumns = useCombined(settings, columnsConfig);

  const sortColumnsModal = useMemo(
    () => (
      <SortColumnsModal
        columnConfigs={combinedColumns}
        open={isSortColumnsModalOpen}
        onConfirm={(newColumnConfig) => {
          dispatch(
            updateColumns({
              columns: newColumnConfig,
              tableId: tableId,
              organizationId,
              userId,
              typeId,
            })
          );
          setIsSortColumnsModalOpen(false);
        }}
        onCancel={() => setIsSortColumnsModalOpen(false)}
      />
    ),
    [
      isSortColumnsModalOpen,
      combinedColumns,
      dispatch,
      tableId,
      organizationId,
      userId,
      typeId,
    ]
  );

  const sortedColumns = useMemo(
    () =>
      combinedColumns
        .filter(({ hidden }) => !hidden)
        .map(
          ({ key, label }): Column<T> => ({
            title: <ColumnTitle title={label} />,
            dataIndex: key,
            ...settings[key],
          })
        ),
    [settings, combinedColumns]
  );

  return {
    sortColumnsModal,
    openSortColumnsModal,
    closeSortColumnsModal,
    sortedColumns,
  };
};

/** Default sort fields on global field order */
const useFieldSort = () => {
  const globalFieldOrder = useAppSelector(globalOrderSelector);

  const globalFieldOrderIndexMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    globalFieldOrder.forEach((field, index) => {
      map[field.id.toString()] = index;
    });
    return map;
  }, [globalFieldOrder]);

  const sortColumnsInFieldOrder = useCallback(
    (a: ColumnConfig, b: ColumnConfig) => {
      const firstFieldIndex =
        globalFieldOrderIndexMap[a.key] ??
        NON_FIELD_DEFAULT_COLUMNS[a.key] ??
        Number.MAX_SAFE_INTEGER;
      const secondFieldIndex =
        globalFieldOrderIndexMap[b.key] ??
        NON_FIELD_DEFAULT_COLUMNS[b.key] ??
        Number.MAX_SAFE_INTEGER;

      return firstFieldIndex - secondFieldIndex;
    },
    [globalFieldOrderIndexMap]
  );

  return sortColumnsInFieldOrder;
};

/** Combine persisted columns with list of all columns */
const useCombined = <K extends ColumnKey = string, T extends object = {}>(
  settings: TColumns<K, T>,
  config: BaseColumnConfig[] | undefined
): ColumnConfig[] => {
  const sortColumnsInFieldOrder = useFieldSort();

  const defaultColumns = useMemo(
    () =>
      Object.entries<TColumn<T>>(settings)
        .map(([key, { label, hidden }]) => ({
          key,
          label,
          hidden,
        }))
        .sort(sortColumnsInFieldOrder),
    [settings, sortColumnsInFieldOrder]
  );

  return useMemo(() => {
    if (!config) {
      return defaultColumns.map((col) => ({
        key: col.key,
        label: col.label as string,
        hidden: col.hidden,
      }));
    }

    const existingColumns = config
      .map((cfg) => {
        const column = defaultColumns.find((col) => col.key === cfg.key);
        return { cfg, column };
      })
      .filter(({ column }) => !!column) // Remove columns that don't exist anymore
      .map<ColumnConfig>(({ cfg, column }) => ({
        key: cfg.key,
        hidden: cfg.hidden,
        label: column?.label as string,
      }));

    // Add new columns to the end
    const newColumns: ColumnConfig[] = defaultColumns
      .filter((col) => !config.find((cfg) => cfg.key === col.key))
      .map((col) => ({
        key: col.key,
        label: col.label as string,
        hidden: col.hidden,
      }));

    return [...existingColumns, ...newColumns];
  }, [defaultColumns, config]);
};
